import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useServerStore } from '../../../store/serverStore';
import { useGamingStore } from '../../../store/gamingStore';
import {
  Gamepad2, Loader2, AlertCircle, Check, X, ChevronRight, ChevronLeft,
  Server, Settings2, Palette, Tags, Pencil, Trash2, Play, ExternalLink,
  Wifi, Image, Video, Puzzle, Globe, Boxes, Upload,
} from 'lucide-react';
import { uploadAPI } from '../../../lib/api';

/* ─── Types ─── */
interface McVersionEntry { id: string; type: string; releaseTime: string }
interface LoaderVersionEntry { version: string; label: string; recommended?: boolean; latest?: boolean; stable?: boolean }

interface GameServerTabProps {
  serverId: string;
  onClose: () => void;
}

/* ─── Game type selector cards ─── */
const GAME_TYPES = [
  {
    id: 'minecraft',
    name: 'Minecraft',
    description: 'Java Edition',
    icon: Boxes,
    available: true,
  },
  {
    id: 'coming_soon',
    name: 'More Games',
    description: 'Coming Soon',
    icon: Gamepad2,
    available: false,
  },
];

/* ─── Stepper steps ─── */
const STEPS = [
  { id: 'basics',      label: 'Basics',      icon: Server },
  { id: 'connection',  label: 'Connection',   icon: Wifi },
  { id: 'appearance',  label: 'Appearance',   icon: Palette },
  { id: 'advanced',    label: 'Advanced',     icon: Settings2 },
];

const GameServerTab: React.FC<GameServerTabProps> = ({ serverId, onClose }) => {
  const { currentServer } = useServerStore();
  const { setGamingHubActive, setGamingView, fetchProfiles, openProfile } = useGamingStore();

  // Loading / data state
  const [loading, setLoading] = useState(true);
  const [hasGameServer, setHasGameServer] = useState(false);
  const [gameServerData, setGameServerData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Game type selection
  const [selectedGame, setSelectedGame] = useState<string | null>(null);

  // Stepper
  const [currentStep, setCurrentStep] = useState(0);

  // Form fields
  const [mcName, setMcName] = useState('');
  const [mcDescription, setMcDescription] = useState('');
  const [mcVersion, setMcVersion] = useState('');
  const [mcModLoader, setMcModLoader] = useState('vanilla');
  const [mcModLoaderVersion, setMcModLoaderVersion] = useState('');
  const [mcAddress, setMcAddress] = useState('');
  const [mcPort, setMcPort] = useState('25565');
  const [mcAutoConnect, setMcAutoConnect] = useState(true);
  const [mcIconUrl, setMcIconUrl] = useState('');
  const [mcBannerUrl, setMcBannerUrl] = useState('');
  const [mcVideoUrl, setMcVideoUrl] = useState('');
  const [mcTags, setMcTags] = useState('');
  const [allowUserMods, setAllowUserMods] = useState(true);
  const [publishInDirectory, setPublishInDirectory] = useState(false);

  // Dynamic version data
  const [mcVersions, setMcVersions] = useState<McVersionEntry[]>([]);
  const [loadingMcVersions, setLoadingMcVersions] = useState(false);
  const [mcVersionFilter, setMcVersionFilter] = useState<'all' | 'release' | 'snapshot'>('release');
  const [mcVersionSearch, setMcVersionSearch] = useState('');
  const [loaderVersions, setLoaderVersions] = useState<LoaderVersionEntry[]>([]);
  const [loadingLoaderVersions, setLoadingLoaderVersions] = useState(false);

  // Editing mode for existing servers
  const [isEditing, setIsEditing] = useState(false);

  /* ─── Fetch existing game server ─── */
  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('access_token');
        if (!token) throw new Error('Not authorized');
        const res = await axios.get(
          `${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setGameServerData(res.data);
        setHasGameServer(true);
        setSelectedGame('minecraft');
        // Populate form
        setMcName(res.data.name || '');
        setMcDescription(res.data.description || '');
        setMcVersion(res.data.version || '');
        setMcModLoader(res.data.mod_loader || 'vanilla');
        setMcModLoaderVersion(res.data.mod_loader_version || '');
        setMcAddress(res.data.address || '');
        setMcPort(res.data.port?.toString() || '25565');
        setMcAutoConnect(res.data.auto_connect ?? true);
        setMcIconUrl(res.data.icon_url || '');
        setMcBannerUrl(res.data.banner_url || '');
        setMcVideoUrl(res.data.video_background_url || '');
        setMcTags(Array.isArray(res.data.tags) ? res.data.tags.join(', ') : '');
        setAllowUserMods(res.data.allow_user_mods !== undefined ? !!res.data.allow_user_mods : true);
        setPublishInDirectory(res.data.publish_in_directory !== undefined ? !!res.data.publish_in_directory : false);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setHasGameServer(false);
          setMcName(currentServer?.name || '');
          setMcDescription(currentServer?.description || '');
        }
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [serverId]);

  /* ─── Fetch MC versions (Electron API) ─── */
  useEffect(() => {
    if (selectedGame !== 'minecraft') return;
    const api = (window as any).api;
    if (!api?.getMinecraftVersions) return;
    setLoadingMcVersions(true);
    api.getMinecraftVersions()
      .then((data: McVersionEntry[]) => setMcVersions(data || []))
      .catch(() => {})
      .finally(() => setLoadingMcVersions(false));
  }, [selectedGame]);

  /* ─── Fetch loader versions when mcVersion/loader change ─── */
  useEffect(() => {
    if (mcModLoader === 'vanilla' || !mcVersion) {
      setLoaderVersions([]);
      return;
    }
    const api = (window as any).api;
    if (!api) return;
    setLoadingLoaderVersions(true);

    const doFetch = async () => {
      try {
        if (mcModLoader === 'forge') {
          const result = await api.getForgeVersions?.(mcVersion);
          if (!result?.success) throw new Error(result?.error || 'Failed');
          const sorted: LoaderVersionEntry[] = (result.versions as any[])
            .map((v: any) => ({
              version: v.forgeVersion,
              label: v.forgeVersion,
              recommended: !!v.recommended,
              latest: !!v.latest,
            }))
            .sort((a: LoaderVersionEntry, b: LoaderVersionEntry) => {
              if (a.recommended && !b.recommended) return -1;
              if (!a.recommended && b.recommended) return 1;
              if (a.latest && !b.latest) return -1;
              if (!a.latest && b.latest) return 1;
              return 0;
            });
          setLoaderVersions(sorted);
          const best = sorted.find((v) => v.recommended) || sorted.find((v) => v.latest) || sorted[0];
          if (best && !mcModLoaderVersion) setMcModLoaderVersion(best.version);
        } else if (mcModLoader === 'fabric') {
          const result = await api.getFabricVersions?.(mcVersion);
          if (!result?.success) throw new Error(result?.error || 'Failed');
          const sorted: LoaderVersionEntry[] = (result.versions as any[])
            .map((v: any) => ({
              version: v.loaderVersion,
              label: v.loaderVersion,
              stable: !!v.stable,
            }))
            .sort((a: LoaderVersionEntry, b: LoaderVersionEntry) => {
              if (a.stable && !b.stable) return -1;
              if (!a.stable && b.stable) return 1;
              return 0;
            });
          setLoaderVersions(sorted);
          const best = sorted.find((v) => v.stable) || sorted[0];
          if (best && !mcModLoaderVersion) setMcModLoaderVersion(best.version);
        }
      } catch {
        setLoaderVersions([]);
      } finally {
        setLoadingLoaderVersions(false);
      }
    };
    doFetch();
  }, [mcModLoader, mcVersion]);

  /* ─── Filtered versions ─── */
  const filteredVersions = useMemo(() => {
    return mcVersions.filter((v) => {
      if (mcVersionFilter !== 'all' && v.type !== mcVersionFilter) return false;
      if (mcVersionSearch && !v.id.toLowerCase().includes(mcVersionSearch.toLowerCase())) return false;
      return true;
    });
  }, [mcVersions, mcVersionFilter, mcVersionSearch]);

  /* ─── Create / Update / Delete ─── */
  const buildPayload = () => ({
    name: mcName,
    description: mcDescription,
    version: mcVersion,
    address: mcAddress || null,
    port: parseInt(mcPort) || 25565,
    mod_loader: mcModLoader,
    mod_loader_version: mcModLoader !== 'vanilla' ? mcModLoaderVersion || null : null,
    icon_url: mcIconUrl || null,
    banner_url: mcBannerUrl || null,
    video_background_url: mcVideoUrl || null,
    tags: mcTags ? mcTags.split(',').map((t: string) => t.trim()).filter(Boolean) : [],
  });

  const handleCreate = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft`,
        buildPayload(),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Also save manifest settings
      await axios.put(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft/manifest`,
        { allow_user_mods: allowUserMods, publish_in_directory: publishInDirectory, auto_connect: mcAutoConnect },
        { headers: { Authorization: `Bearer ${token}` } },
      ).catch(() => {});
      setHasGameServer(true);
      setIsEditing(false);
      setSuccess('Game server created successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create game server');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft`,
        buildPayload(),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await axios.put(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft/manifest`,
        { allow_user_mods: allowUserMods, publish_in_directory: publishInDirectory, auto_connect: mcAutoConnect },
        { headers: { Authorization: `Bearer ${token}` } },
      ).catch(() => {});
      setIsEditing(false);
      setSuccess('Game server updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update game server');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setHasGameServer(false);
      setGameServerData(null);
      setSelectedGame(null);
      setCurrentStep(0);
      setSuccess('Game server removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete game server');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenProfileEditor = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Not authorized');
      if (!window.api) throw new Error('Desktop app required');

      const manifestRes = await axios.get(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/minecraft/manifest`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const manifest = manifestRes.data;

      const listRes = await window.api.profileList();
      if (!listRes?.success) throw new Error('Cannot read local profiles');

      const localManifest = {
        manifest_version: manifest.manifest_version || 1,
        allow_user_mods: true,
        server: {
          address: manifest.server?.address || mcAddress || '',
          port: manifest.server?.port || parseInt(mcPort) || 25565,
          auto_connect: manifest.server?.auto_connect ?? true,
        },
        minecraft: {
          version: manifest.minecraft?.version || mcVersion,
          loader: manifest.minecraft?.loader || mcModLoader,
          loader_version: manifest.minecraft?.loader_version ?? mcModLoaderVersion ?? null,
        },
        mods: (manifest.mods || []).map((m: any) => ({ ...m, required: m.required === 1 || m.required === true })),
        resourcePacks: manifest.resourcePacks || [],
        shaderPacks: manifest.shaderPacks || [],
      };

      const existing = (listRes.data || []).find((p: any) => p.source === 'server' && p.erozion_server_id === serverId);
      let profileId = existing?.id;

      if (!profileId) {
        const createRes = await window.api.profileCreate({
          name: manifest.name || mcName || currentServer?.name || 'Server Profile',
          source: 'server',
          erozion_server_id: serverId,
          ...localManifest,
        });
        if (!createRes?.success || !createRes?.data?.id) throw new Error(createRes?.error || 'Failed to create profile');
        profileId = createRes.data.id;
      } else {
        const syncRes = await window.api.profileSync(profileId, localManifest);
        if (!syncRes?.success) throw new Error(syncRes?.error || 'Failed to sync profile');
      }

      setGamingHubActive(true);
      setGamingView('minecraft');
      await fetchProfiles();
      openProfile(profileId);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to open profile editor');
    }
  };

  /* ─── Render ─── */
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-accent-color" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">Game Server</h2>
        <p className="text-xs text-text-muted mt-0.5">Configure a game server for your community.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-xs">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/30 text-success text-xs">
          <Check className="w-3.5 h-3.5 flex-shrink-0" /> {success}
        </div>
      )}

      {/* ════ Existing server — summary card ════ */}
      {hasGameServer && !isEditing && (
        <div className="space-y-4">
          {/* Hero card */}
          <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl overflow-hidden">
            {/* Banner */}
            <div className="relative h-32">
              {mcBannerUrl ? (
                <img src={mcBannerUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-emerald-600/20 via-accent-color/10 to-transparent" />
              )}
              {/* Video preview overlay */}
              {mcVideoUrl && (
                <div className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-black/60 text-[10px] text-white flex items-center gap-1">
                  <Video className="w-3 h-3" /> Video set
                </div>
              )}
            </div>

            <div className="px-5 py-4 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-bg-tertiary border border-border-color overflow-hidden flex-shrink-0">
                  {mcIconUrl ? (
                    <img src={mcIconUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Boxes className="w-6 h-6 text-text-muted" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-base font-bold text-text-primary">{mcName}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-medium">
                      Minecraft {mcVersion}
                    </span>
                    {mcModLoader !== 'vanilla' && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 font-medium capitalize">
                        {mcModLoader}
                      </span>
                    )}
                    {mcAddress && (
                      <span className="text-[11px] text-text-muted flex items-center gap-1">
                        <Wifi className="w-3 h-3" /> {mcAddress}:{mcPort}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setIsEditing(true); setCurrentStep(0); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-bg-tertiary hover:bg-highlight/50 text-text-secondary hover:text-text-primary border border-border-color transition-all"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-bg-tertiary hover:bg-error/10 text-text-secondary hover:text-error border border-border-color transition-all disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
            </div>
          </div>

          {/* Quick info cards */}
          <div className="grid grid-cols-3 gap-3">
            <InfoCard icon={Settings2} label="Mod Loader" value={mcModLoader === 'vanilla' ? 'Vanilla' : `${mcModLoader} ${mcModLoaderVersion || ''}`} />
            <InfoCard icon={Puzzle} label="User Mods" value={allowUserMods ? 'Allowed' : 'Disabled'} />
            <InfoCard icon={Globe} label="Directory" value={publishInDirectory ? 'Published' : 'Private'} />
          </div>

          {/* Profile editor link */}
          {(window as any).api && (
            <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">Profile Editor</p>
                <p className="text-xs text-text-muted mt-0.5">Manage mods, resource packs, and shaders in the gaming hub.</p>
              </div>
              <button
                onClick={handleOpenProfileEditor}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-color text-black text-xs font-bold hover:bg-accent-hover transition-all"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open Editor
              </button>
            </div>
          )}
        </div>
      )}

      {/* ════ No game server — selection ════ */}
      {!hasGameServer && !selectedGame && (
        <div className="space-y-6">
          {/* Empty state */}
          <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-accent-color/10 flex items-center justify-center mb-4">
              <Gamepad2 className="w-8 h-8 text-accent-color" />
            </div>
            <h3 className="text-base font-bold text-text-primary mb-1">Set Up a Game Server</h3>
            <p className="text-xs text-text-muted max-w-sm mx-auto">
              Link a game server to your community. Members will be able to install it, 
              join games, and receive automatic updates.
            </p>
          </div>

          {/* Game type cards */}
          <div className="grid grid-cols-2 gap-3">
            {GAME_TYPES.map((game) => {
              const Icon = game.icon;
              return (
                <button
                  key={game.id}
                  onClick={() => game.available && setSelectedGame(game.id)}
                  disabled={!game.available}
                  className={`bg-bg-secondary/80 backdrop-blur-sm border rounded-xl p-5 text-left transition-all ${
                    game.available
                      ? 'border-border-color hover:border-accent-color hover:bg-highlight/30 cursor-pointer'
                      : 'border-border-color/50 opacity-40 cursor-not-allowed'
                  }`}
                >
                  <div className="w-10 h-10 rounded-xl bg-accent-color/10 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-accent-color" />
                  </div>
                  <p className="text-sm font-bold text-text-primary">{game.name}</p>
                  <p className="text-[11px] text-text-muted mt-0.5">{game.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ════ Stepper form (create or edit) ════ */}
      {((selectedGame === 'minecraft' && !hasGameServer) || isEditing) && (
        <div className="space-y-5">
          {/* Step indicators */}
          <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4">
            <div className="flex items-center justify-between">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                const isActive = idx === currentStep;
                const isDone = idx < currentStep;
                return (
                  <React.Fragment key={step.id}>
                    <button
                      onClick={() => setCurrentStep(idx)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all ${
                        isActive ? 'bg-accent-color/10 text-accent-color' :
                        isDone ? 'text-success' :
                        'text-text-muted hover:text-text-secondary'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        isActive ? 'bg-accent-color text-black' :
                        isDone ? 'bg-success text-black' :
                        'bg-bg-tertiary border border-border-color'
                      }`}>
                        {isDone ? <Check className="w-3 h-3" /> : idx + 1}
                      </div>
                      <span className="text-xs font-medium hidden sm:block">{step.label}</span>
                    </button>
                    {idx < STEPS.length - 1 && (
                      <div className={`flex-1 h-px mx-2 ${idx < currentStep ? 'bg-success' : 'bg-border-color'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Step content */}
          <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-5">
            {currentStep === 0 && (
              <StepBasics
                mcName={mcName} setMcName={setMcName}
                mcDescription={mcDescription} setMcDescription={setMcDescription}
                mcVersion={mcVersion} setMcVersion={setMcVersion}
                mcModLoader={mcModLoader} setMcModLoader={setMcModLoader}
                mcModLoaderVersion={mcModLoaderVersion} setMcModLoaderVersion={setMcModLoaderVersion}
                mcVersions={filteredVersions}
                loadingMcVersions={loadingMcVersions}
                mcVersionFilter={mcVersionFilter} setMcVersionFilter={setMcVersionFilter}
                mcVersionSearch={mcVersionSearch} setMcVersionSearch={setMcVersionSearch}
                loaderVersions={loaderVersions}
                loadingLoaderVersions={loadingLoaderVersions}
              />
            )}
            {currentStep === 1 && (
              <StepConnection
                mcAddress={mcAddress} setMcAddress={setMcAddress}
                mcPort={mcPort} setMcPort={setMcPort}
                mcAutoConnect={mcAutoConnect} setMcAutoConnect={setMcAutoConnect}
              />
            )}
            {currentStep === 2 && (
              <StepAppearance
                serverId={serverId}
                mcIconUrl={mcIconUrl} setMcIconUrl={setMcIconUrl}
                mcBannerUrl={mcBannerUrl} setMcBannerUrl={setMcBannerUrl}
                mcVideoUrl={mcVideoUrl} setMcVideoUrl={setMcVideoUrl}
              />
            )}
            {currentStep === 3 && (
              <StepAdvanced
                mcTags={mcTags} setMcTags={setMcTags}
                allowUserMods={allowUserMods} setAllowUserMods={setAllowUserMods}
                publishInDirectory={publishInDirectory} setPublishInDirectory={setPublishInDirectory}
              />
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                if (currentStep > 0) setCurrentStep((s) => s - 1);
                else if (!hasGameServer) setSelectedGame(null);
                else setIsEditing(false);
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-text-secondary hover:text-text-primary bg-bg-tertiary hover:bg-highlight/50 border border-border-color transition-all"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              {currentStep > 0 ? 'Previous' : 'Back'}
            </button>

            {currentStep < STEPS.length - 1 ? (
              <button
                onClick={() => setCurrentStep((s) => s + 1)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-accent-color text-black hover:bg-accent-hover transition-all"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={hasGameServer ? handleUpdate : handleCreate}
                disabled={isSubmitting || !mcName.trim() || !mcVersion}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-bold bg-accent-color text-black hover:bg-accent-hover transition-all disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {isSubmitting ? 'Saving...' : hasGameServer ? 'Save Changes' : 'Create Game Server'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   STEP COMPONENTS
   ════════════════════════════════════════ */

const Field: React.FC<{ label: string; htmlFor?: string; hint?: string; children: React.ReactNode }> = ({ label, htmlFor, hint, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-xs font-semibold text-text-secondary mb-1.5">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-text-muted mt-1">{hint}</p>}
  </div>
);

/* ─── Step 1: Basics ─── */
const StepBasics: React.FC<{
  mcName: string; setMcName: (v: string) => void;
  mcDescription: string; setMcDescription: (v: string) => void;
  mcVersion: string; setMcVersion: (v: string) => void;
  mcModLoader: string; setMcModLoader: (v: string) => void;
  mcModLoaderVersion: string; setMcModLoaderVersion: (v: string) => void;
  mcVersions: McVersionEntry[];
  loadingMcVersions: boolean;
  mcVersionFilter: string; setMcVersionFilter: (v: 'all' | 'release' | 'snapshot') => void;
  mcVersionSearch: string; setMcVersionSearch: (v: string) => void;
  loaderVersions: LoaderVersionEntry[];
  loadingLoaderVersions: boolean;
}> = (props) => {
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [showLoaderDropdown, setShowLoaderDropdown] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Server className="w-4 h-4 text-accent-color" />
        <h3 className="text-sm font-bold text-text-primary">Basic Configuration</h3>
      </div>

      <Field label="Server Name" htmlFor="gs-name">
        <input
          id="gs-name"
          type="text"
          value={props.mcName}
          onChange={(e) => props.setMcName(e.target.value)}
          className="settings-input"
          placeholder="My Minecraft Server"
          required
        />
      </Field>

      <Field label="Description" htmlFor="gs-desc">
        <textarea
          id="gs-desc"
          value={props.mcDescription}
          onChange={(e) => props.setMcDescription(e.target.value)}
          className="settings-input min-h-[60px] resize-y"
          placeholder="Describe your server..."
          rows={2}
        />
      </Field>

      {/* Minecraft Version — searchable dropdown */}
      <Field label="Minecraft Version">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowVersionDropdown(!showVersionDropdown)}
            className="settings-input w-full text-left flex items-center justify-between"
          >
            <span>{props.mcVersion || 'Select a version...'}</span>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showVersionDropdown ? 'rotate-90' : ''}`} />
          </button>

          {showVersionDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowVersionDropdown(false)} />
              <div className="absolute z-20 mt-1 w-full bg-bg-primary border border-border-color rounded-xl shadow-xl overflow-hidden">
                {/* Filters */}
                <div className="p-2 border-b border-border-color space-y-2">
                  <input
                    type="text"
                    value={props.mcVersionSearch}
                    onChange={(e) => props.setMcVersionSearch(e.target.value)}
                    className="settings-input text-xs"
                    placeholder="Search version..."
                    autoFocus
                  />
                  <div className="flex gap-1">
                    {(['release', 'snapshot', 'all'] as const).map((f) => (
                      <button
                        key={f}
                        onClick={() => props.setMcVersionFilter(f)}
                        className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${
                          props.mcVersionFilter === f
                            ? 'bg-accent-color/10 text-accent-color'
                            : 'text-text-muted hover:text-text-secondary hover:bg-highlight/50'
                        }`}
                      >
                        {f === 'all' ? 'All' : f === 'release' ? 'Releases' : 'Snapshots'}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Version list */}
                <div className="max-h-48 overflow-y-auto gaming-scrollbar">
                  {props.loadingMcVersions ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin text-accent-color" />
                    </div>
                  ) : props.mcVersions.length > 0 ? (
                    props.mcVersions.slice(0, 100).map((v) => (
                      <button
                        key={v.id}
                        onClick={() => {
                          props.setMcVersion(v.id);
                          setShowVersionDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs transition-all ${
                          props.mcVersion === v.id
                            ? 'bg-accent-color/10 text-accent-color font-medium'
                            : 'text-text-secondary hover:text-text-primary hover:bg-highlight/50'
                        }`}
                      >
                        <span>{v.id}</span>
                        <span className="ml-2 text-[10px] text-text-muted">{v.type}</span>
                      </button>
                    ))
                  ) : (
                    <p className="text-xs text-text-muted p-3 text-center">
                      {(window as any).api ? 'No versions found.' : 'Version list requires the desktop app.'}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Field>

      {/* Mod Loader */}
      <Field label="Mod Loader">
        <div className="grid grid-cols-3 gap-2">
          {['vanilla', 'forge', 'fabric'].map((loader) => (
            <button
              key={loader}
              type="button"
              onClick={() => {
                props.setMcModLoader(loader);
                props.setMcModLoaderVersion('');
              }}
              className={`px-3 py-2.5 rounded-lg text-xs font-medium border transition-all capitalize ${
                props.mcModLoader === loader
                  ? 'bg-accent-color/10 border-accent-color text-accent-color'
                  : 'bg-bg-tertiary border-border-color text-text-secondary hover:text-text-primary hover:border-text-muted/40'
              }`}
            >
              {loader}
            </button>
          ))}
        </div>
      </Field>

      {/* Loader Version */}
      {props.mcModLoader !== 'vanilla' && (
        <Field label={`${props.mcModLoader.charAt(0).toUpperCase() + props.mcModLoader.slice(1)} Version`}>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowLoaderDropdown(!showLoaderDropdown)}
              className="settings-input w-full text-left flex items-center justify-between"
            >
              <span>
                {props.loadingLoaderVersions ? 'Loading...' :
                 props.mcModLoaderVersion || 'Select a version...'}
              </span>
              <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showLoaderDropdown ? 'rotate-90' : ''}`} />
            </button>

            {showLoaderDropdown && !props.loadingLoaderVersions && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowLoaderDropdown(false)} />
                <div className="absolute z-20 mt-1 w-full bg-bg-primary border border-border-color rounded-xl shadow-xl max-h-48 overflow-y-auto gaming-scrollbar">
                  {props.loaderVersions.length > 0 ? (
                    props.loaderVersions.map((v) => (
                      <button
                        key={v.version}
                        onClick={() => {
                          props.setMcModLoaderVersion(v.version);
                          setShowLoaderDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs transition-all flex items-center justify-between ${
                          props.mcModLoaderVersion === v.version
                            ? 'bg-accent-color/10 text-accent-color font-medium'
                            : 'text-text-secondary hover:text-text-primary hover:bg-highlight/50'
                        }`}
                      >
                        <span>{v.label}</span>
                        <span className="flex gap-1">
                          {v.recommended && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-success/10 text-success">Recommended</span>
                          )}
                          {v.latest && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-400">Latest</span>
                          )}
                          {v.stable && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-success/10 text-success">Stable</span>
                          )}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="text-xs text-text-muted p-3 text-center">No versions available.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </Field>
      )}
    </div>
  );
};

/* ─── Step 2: Connection ─── */
const StepConnection: React.FC<{
  mcAddress: string; setMcAddress: (v: string) => void;
  mcPort: string; setMcPort: (v: string) => void;
  mcAutoConnect: boolean; setMcAutoConnect: (v: boolean) => void;
}> = (props) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 mb-2">
      <Wifi className="w-4 h-4 text-accent-color" />
      <h3 className="text-sm font-bold text-text-primary">Server Connection</h3>
    </div>

    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-2">
        <Field label="Server Address" htmlFor="gs-addr" hint="IP address or domain name (optional).">
          <input
            id="gs-addr"
            type="text"
            value={props.mcAddress}
            onChange={(e) => props.setMcAddress(e.target.value)}
            className="settings-input"
            placeholder="mc.example.com"
          />
        </Field>
      </div>
      <Field label="Port" htmlFor="gs-port">
        <input
          id="gs-port"
          type="text"
          value={props.mcPort}
          onChange={(e) => props.setMcPort(e.target.value)}
          className="settings-input"
          placeholder="25565"
        />
      </Field>
    </div>

    <div className="bg-bg-tertiary/50 rounded-xl p-4">
      <label className="flex items-center justify-between cursor-pointer">
        <div>
          <p className="text-xs font-medium text-text-primary">Auto-connect</p>
          <p className="text-[10px] text-text-muted mt-0.5">Automatically connect players to the server on launch.</p>
        </div>
        <Toggle checked={props.mcAutoConnect} onChange={props.setMcAutoConnect} />
      </label>
    </div>
  </div>
);

/* ─── Step 3: Appearance ─── */
const StepAppearance: React.FC<{
  serverId: string;
  mcIconUrl: string; setMcIconUrl: (v: string) => void;
  mcBannerUrl: string; setMcBannerUrl: (v: string) => void;
  mcVideoUrl: string; setMcVideoUrl: (v: string) => void;
}> = (props) => {
  const iconFileRef = useRef<HTMLInputElement>(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleIconFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIcon(true);
    setUploadError(null);
    try {
      const { url } = await uploadAPI.uploadGameServerIcon(props.serverId, file);
      props.setMcIconUrl(url);
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingIcon(false);
      if (iconFileRef.current) iconFileRef.current.value = '';
    }
  };

  return (
  <div className="space-y-4">
    <div className="flex items-center gap-2 mb-2">
      <Palette className="w-4 h-4 text-accent-color" />
      <h3 className="text-sm font-bold text-text-primary">Appearance</h3>
    </div>

    {uploadError && (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-xs">
        <X className="w-3.5 h-3.5 flex-shrink-0" /> {uploadError}
      </div>
    )}

    <input ref={iconFileRef} type="file" accept="image/*" className="hidden" onChange={handleIconFile} />
    <Field label="Server Icon" htmlFor="gs-icon" hint="Square image for the server icon.">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-bg-tertiary border border-border-color overflow-hidden flex-shrink-0">
          {props.mcIconUrl ? (
            <img src={props.mcIconUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Image className="w-4 h-4 text-text-muted" />
            </div>
          )}
        </div>
        <button
          type="button"
          id="gs-icon"
          onClick={() => iconFileRef.current?.click()}
          disabled={uploadingIcon}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-bg-tertiary border border-border-color text-xs font-medium text-text-secondary hover:text-text-primary hover:border-accent-color/50 transition-all disabled:opacity-50"
        >
          {uploadingIcon ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploadingIcon ? 'Uploading...' : 'Upload Icon'}
        </button>
      </div>
    </Field>

    <Field label="Banner URL" htmlFor="gs-banner" hint="Wide image for the server banner.">
      <input
        id="gs-banner"
        type="url"
        value={props.mcBannerUrl}
        onChange={(e) => props.setMcBannerUrl(e.target.value)}
        className="settings-input"
        placeholder="https://example.com/banner.png"
      />
      {props.mcBannerUrl && (
        <div className="mt-2 h-20 rounded-lg overflow-hidden border border-border-color">
          <img src={props.mcBannerUrl} alt="" className="w-full h-full object-cover" />
        </div>
      )}
    </Field>

    <Field label="Background Video URL" htmlFor="gs-video" hint="MP4 video for the background (R2 upload coming soon).">
      <input
        id="gs-video"
        type="url"
        value={props.mcVideoUrl}
        onChange={(e) => props.setMcVideoUrl(e.target.value)}
        className="settings-input"
        placeholder="https://example.com/background.mp4"
      />
      {props.mcVideoUrl && (
        <div className="mt-2 h-28 rounded-lg overflow-hidden border border-border-color bg-black">
          <video
            src={props.mcVideoUrl}
            className="w-full h-full object-cover"
            muted
            loop
            autoPlay
            playsInline
          />
        </div>
      )}
    </Field>
  </div>
  );
};

/* ─── Step 4: Advanced ─── */
const StepAdvanced: React.FC<{
  mcTags: string; setMcTags: (v: string) => void;
  allowUserMods: boolean; setAllowUserMods: (v: boolean) => void;
  publishInDirectory: boolean; setPublishInDirectory: (v: boolean) => void;
}> = (props) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 mb-2">
      <Settings2 className="w-4 h-4 text-accent-color" />
      <h3 className="text-sm font-bold text-text-primary">Advanced Settings</h3>
    </div>

    <Field label="Tags" htmlFor="gs-tags" hint="Comma-separated tags (e.g. survival, pvp, creative).">
      <input
        id="gs-tags"
        type="text"
        value={props.mcTags}
        onChange={(e) => props.setMcTags(e.target.value)}
        className="settings-input"
        placeholder="survival, pvp, vanilla"
      />
    </Field>

    <div className="space-y-2">
      <div className="bg-bg-tertiary/50 rounded-xl p-4">
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <p className="text-xs font-medium text-text-primary">Allow User Mods</p>
            <p className="text-[10px] text-text-muted mt-0.5">Let players add their own mods to the profile.</p>
          </div>
          <Toggle checked={props.allowUserMods} onChange={props.setAllowUserMods} />
        </label>
      </div>

      <div className="bg-bg-tertiary/50 rounded-xl p-4">
        <label className="flex items-center justify-between cursor-pointer">
          <div>
            <p className="text-xs font-medium text-text-primary">Publish in Directory</p>
            <p className="text-[10px] text-text-muted mt-0.5">Make your server visible in the public directory.</p>
          </div>
          <Toggle checked={props.publishInDirectory} onChange={props.setPublishInDirectory} />
        </label>
      </div>
    </div>
  </div>
);

/* ═══ Reusable components ═══ */

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
  <div
    onClick={() => onChange(!checked)}
    className={`w-9 h-5 rounded-full flex items-center transition-all cursor-pointer ${
      checked ? 'bg-accent-color justify-end' : 'bg-bg-tertiary border border-border-color justify-start'
    }`}
  >
    <div className={`w-3.5 h-3.5 rounded-full mx-0.5 transition-all ${
      checked ? 'bg-black' : 'bg-text-muted'
    }`} />
  </div>
);

const InfoCard: React.FC<{ icon: React.ElementType; label: string; value: string }> = ({ icon: Icon, label, value }) => (
  <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-3">
    <div className="flex items-center gap-2 mb-1.5">
      <Icon className="w-3.5 h-3.5 text-accent-color" />
      <span className="text-[10px] font-bold text-text-muted uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-xs font-medium text-text-primary capitalize">{value}</p>
  </div>
);

const ChevronRight2 = ChevronRight; // alias for JSX usage

export default GameServerTab;
